import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"

const filterChipVariants = cva(
  [
    "inline-flex items-center gap-1.5",
    "h-control px-2 rounded-md border text-sm",
    "transition-all",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coval-info-border focus-visible:ring-offset-1",
  ],
  {
    variants: {
      selected: {
        true: "border-transparent text-white shadow-sm",
        false:
          "border-coval-border text-coval-text-muted hover:border-coval-border-active hover:text-coval-text",
      },
    },
    defaultVariants: {
      selected: false,
    },
  },
)

export interface FilterChipProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof filterChipVariants> {
  /**
   * Accent color (any CSS color string, including `var(--token)`). Fills the
   * chip when selected and tints the border + leading dot when not. Omit for a
   * neutral chip.
   */
  color?: string
  /** Optional count rendered as "×N" after the label. */
  count?: number
}

const FilterChip = React.forwardRef<HTMLButtonElement, FilterChipProps>(
  (
    {
      className,
      selected: selectedProp,
      color,
      count,
      children,
      type,
      style,
      ...props
    },
    ref,
  ) => {
    const selected = selectedProp ?? false
    // Unselected chips get a translucent tint of the accent. color-mix keeps
    // that working for any CSS color form — appending a hex alpha pair would
    // only be valid for 6-digit hex and silently voids `var(--token)` accents.
    const colorStyle: React.CSSProperties = color
      ? selected
        ? { backgroundColor: color }
        : { borderColor: `color-mix(in srgb, ${color} 38%, transparent)` }
      : {}

    return (
      <button
        ref={ref}
        type={type ?? "button"}
        aria-pressed={selected}
        className={cn(filterChipVariants({ selected, className }))}
        style={{ ...colorStyle, ...style }}
        {...props}
      >
        {color !== undefined && (
          <span
            className="size-2 rounded-sm flex-none"
            style={{ backgroundColor: selected ? "#fff" : color }}
            aria-hidden="true"
          />
        )}
        <span className="font-medium">{children}</span>
        {count !== undefined && (
          <span className={selected ? "opacity-80" : "opacity-60"}>
            ×{count}
          </span>
        )}
      </button>
    )
  },
)

FilterChip.displayName = "FilterChip"

export { FilterChip, filterChipVariants }
